/**
 * ChapterSpine — a single book spine on the Bookshelf.
 *
 * Restrained, museum-like, NOT skeuomorphic. The cloth surface, foil
 * details and lean angle come from the server (deterministic per
 * chapter_id) so every device renders the same shelf. Frontend never
 * rolls its own randomness.
 *
 * Interaction rules per the brand:
 *   • NO hover explosions
 *   • NO 3D rotation
 *   • Subtle ambient breathing (slow box-shadow respiration)
 *   • Hover lifts 2-3px only — the shelf reads as a quiet object,
 *     not a clickable button
 *
 * Language:
 *   • "Pull from the shelf"  (CTA)
 *   • "Revisit this chapter" (accessibility label)
 *   • Never "Open" / "Manage" / "Download"
 */
import React, { useMemo } from 'react';

const CLOTH_GRADIENTS = {
  midnight: 'linear-gradient(180deg, #15161B 0%, #0c1126 100%)',
  burgundy: 'linear-gradient(180deg, #4a1b25 0%, #2a0e16 100%)',
  forest:   'linear-gradient(180deg, #1f3a2c 0%, #0e1f17 100%)',
  ink:      'linear-gradient(180deg, #1c1b2a 0%, #0a0913 100%)',
  umber:    'linear-gradient(180deg, #3d2818 0%, #20140b 100%)',
};
const CLOTH_FOIL = {
  midnight: '#D4AF5E',
  burgundy: '#E6B855',
  forest:   '#C9A86E',
  ink:      '#D9B66A',
  umber:    '#E0BB6A',
};
// Subtle cloth weave noise via inset shadow — feels tactile without
// loading textures. Each cloth gets a slightly different weave key.
const CLOTH_WEAVE = {
  midnight: 'inset 0 0 0 1px rgba(255,235,210,0.04), inset 1px 0 0 rgba(255,235,210,0.06)',
  burgundy: 'inset 0 0 0 1px rgba(255,235,210,0.06), inset 1px 0 0 rgba(255,235,210,0.08)',
  forest:   'inset 0 0 0 1px rgba(255,235,210,0.05), inset 1px 0 0 rgba(255,235,210,0.07)',
  ink:      'inset 0 0 0 1px rgba(255,235,210,0.04), inset 1px 0 0 rgba(255,235,210,0.05)',
  umber:    'inset 0 0 0 1px rgba(255,235,210,0.05), inset 1px 0 0 rgba(255,235,210,0.07)',
};

export default function ChapterSpine({
  chapter,
  shelfHeight = 360,
  onPull,
  entranceDelay = 0,
  focused = false,
}) {
  const spine = chapter?.spine || {};
  const cloth = spine.cloth || 'midnight';
  const foil = CLOTH_FOIL[cloth] || '#D4AF5E';
  const height = Math.round((spine.height_pct || 0.88) * shelfHeight);
  const thickness = Math.max(20, Math.min(60, spine.thickness_px || 28));
  const lean = spine.lean_deg || 0;
  const titleOffset = spine.title_offset_px || 0;

  const fontSizeTitle = thickness >= 30 ? 13 : 12;

  // When this spine is the focus target of a "settled on the shelf"
  // transition, layer a soft outer glow that pulses three times then
  // fades — long enough for the host to notice "ah, THAT one", short
  // enough to not overstay.
  const focusedShadow = focused
    ? `, 0 0 28px 4px ${foil}55, 0 0 0 1px ${foil}88`
    : '';
  const focusedAnim = focused
    ? `, spineFocusGlow 2.6s ease-out ${entranceDelay + 200}ms 3 both`
    : '';

  const style = useMemo(() => ({
    position: 'relative',
    width: thickness,
    height,
    flex: `0 0 ${thickness}px`,
    background: CLOTH_GRADIENTS[cloth] || CLOTH_GRADIENTS.midnight,
    boxShadow: `
      ${CLOTH_WEAVE[cloth] || ''},
      0 8px 18px rgba(0,0,0,0.45),
      0 0 0 1px rgba(0,0,0,0.4),
      inset 0 14px 22px rgba(0,0,0,0.25),
      inset 0 -10px 18px rgba(0,0,0,0.30)
      ${focusedShadow}
    `,
    transform: `rotate(${lean}deg) translateY(${titleOffset}px)`,
    transformOrigin: 'bottom center',
    transition: 'transform 480ms cubic-bezier(0.22, 0.61, 0.36, 1), box-shadow 480ms ease, filter 480ms ease',
    cursor: 'pointer',
    borderRadius: '2px 2px 1px 1px',
    overflow: 'hidden',
    animation: `spineEnter 700ms cubic-bezier(0.22, 0.61, 0.36, 1) ${entranceDelay}ms both, spineBreathe 7s ease-in-out ${entranceDelay + 800}ms infinite${focusedAnim}`,
  }), [thickness, height, cloth, lean, titleOffset, entranceDelay, focused, focusedShadow, focusedAnim]);

  return (
    <button
      type="button"
      onClick={() => onPull && onPull(chapter)}
      data-testid={`shelf-spine-${chapter.id}`}
      aria-label={`Revisit chapter ${chapter.name}`}
      style={{
        background: 'transparent', border: 'none', padding: 0, margin: 0,
        display: 'inline-block',
      }}
      onMouseEnter={(e) => {
        const btn = e.currentTarget.querySelector('[data-spine]');
        if (btn) {
          btn.style.transform = `rotate(${lean * 0.5}deg) translateY(-3px)`;
          btn.style.filter = 'brightness(1.08)';
        }
      }}
      onMouseLeave={(e) => {
        const btn = e.currentTarget.querySelector('[data-spine]');
        if (btn) {
          btn.style.transform = `rotate(${lean}deg) translateY(${titleOffset}px)`;
          btn.style.filter = 'none';
        }
      }}
    >
      <div data-spine style={style}>
        {/* Top + bottom foil bands — band thickness varies by foil pattern */}
        <div style={{
          position: 'absolute', top: 12, left: 0, right: 0, height: 2,
          background: foil, opacity: spine.foil === 'embossed' ? 0.85 : 0.55,
          boxShadow: spine.foil === 'fleck' ? `0 1px 0 ${foil}33` : 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: 16, left: 0, right: 0, height: 1.5,
          background: foil, opacity: spine.foil === 'embossed' ? 0.85 : 0.50,
        }} />
        {/* Optional foil imperfection — tiny tarnished fleck for 'imperfect' */}
        {spine.foil === 'imperfect' && (
          <div style={{
            position: 'absolute', top: 22, left: '50%', width: 3, height: 3,
            transform: 'translateX(-50%) rotate(15deg)',
            background: foil, opacity: 0.35, borderRadius: '50%',
            filter: 'blur(0.3px)',
          }} />
        )}

        {/* Title — printed sideways, serif, foil-coloured. Reads from bottom→top. */}
        <div style={{
          position: 'absolute',
          inset: '32px 0 36px 0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          writingMode: 'vertical-rl',
          transform: 'rotate(180deg)',
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontSize: fontSizeTitle,
          fontWeight: 500,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: foil,
          textShadow: `0 0 8px ${foil}22`,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          padding: '0 2px',
        }}>
          {chapter.name}
        </div>
      </div>
    </button>
  );
}
